import React from "react";
import { WizardData } from "../index";
import LocationInput from "../../../components/LocationInput";
import { useAutoResizeTextarea } from "../../../hooks/useAutoResizeTextarea";

interface PricingStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
  darkMode: boolean;
}

const MIN_DESCRIPTION_LENGTH = 50;
const DESCRIPTION_TEXTAREA_MAX_HEIGHT = 320;

const PricingStep: React.FC<PricingStepProps> = ({
  data,
  updateData,
  onNext,
  onBack,
  darkMode,
}) => {
  const descriptionLength = data.description.length;
  const meetsMinDescription = descriptionLength >= MIN_DESCRIPTION_LENGTH;
  const descriptionRef = useAutoResizeTextarea(
    data.description,
    DESCRIPTION_TEXTAREA_MAX_HEIGHT
  );

  const handleNext = () => {
    const price = parseFloat(data.price);
    if (!data.price || isNaN(price) || price < 100) {
      alert("Please enter a valid price (minimum $100)");
      return;
    }
    if (!meetsMinDescription) {
      alert(`Please write at least ${MIN_DESCRIPTION_LENGTH} characters in your description`);
      return;
    }
    if (!data.location) {
      alert("Please select a location for your listing");
      return;
    }
    onNext();
  };

  return (
    <div className="step-container">
      <h2 className="step-title">Set Your Price</h2>
      <p className="step-subtitle">
        Add pricing details and describe your vehicle
      </p>

      <div className="wizard-form-group">
        <label htmlFor="price">Asking Price *</label>
        <div style={{ position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: darkMode ? "#888" : "#666",
              fontSize: "16px",
            }}
          >
            $
          </span>
          <input
            type="number"
            id="price"
            className="wizard-input"
            value={data.price}
            onChange={(e) => updateData({ price: e.target.value })}
            placeholder="ex. 25000"
            min="100"
            step="100"
            style={{ paddingLeft: "32px" }}
            required
          />
        </div>
        <p style={{ 
          fontSize: "12px", 
          color: darkMode ? "#888" : "#666", 
          marginTop: "4px" 
        }}>
          Minimum $100
        </p>
      </div>

      <div className="wizard-form-group">
        <label htmlFor="description">
          Description *
          {!meetsMinDescription && (
            <span
              style={{
                fontWeight: "normal",
                color: darkMode ? "#888" : "#666",
              }}
            >
              {" "}
              ({descriptionLength}/{MIN_DESCRIPTION_LENGTH} min characters)
            </span>
          )}
        </label>
        <textarea
          ref={descriptionRef}
          id="description"
          className="wizard-textarea wizard-textarea-autoresize"
          value={data.description}
          onChange={(e) => updateData({ description: e.target.value })}
          placeholder="Describe your vehicle's condition, history, features, and any recent maintenance or upgrades. Be honest about any issues to build trust with buyers."
          rows={5}
          required
        />
        <p style={{ 
          fontSize: "12px", 
          color: darkMode ? "#888" : "#666", 
          marginTop: "4px" 
        }}>
          Tip: Mention service history, new parts, reason for selling
        </p>
      </div>

      <LocationInput
        onLocationSelect={(location) => updateData({ location })}
        initialLocation={data.location || undefined}
        darkMode={darkMode}
      />

      <div className="wizard-buttons">
        <button className="wizard-btn wizard-btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
        >
          Review Listing
        </button>
      </div>
    </div>
  );
};

export default PricingStep;
